import React, { useCallback, useEffect } from 'react';

import config from 'config';
import styled, { css } from 'styled-components';

import { PromoMessage } from '~/shared/components/PromoMessage';
import featureNames from '~/shared/constants/featureFlags';
import { getPromoPricingActiveAsset } from '~/shared/utils/getPromoPricingActiveAsset';
import { getPromoPricingUIAssetContainerName } from '~/shared/utils/promoPricingUI';
import useHasFeature from '~/shared/utils/useHasFeature';
import { useMembership } from '~/techstyle-shared/react-accounts';
import { useAsset } from '~/techstyle-shared/react-assets';
import { Button, useProductContext } from '~/techstyle-shared/react-components';
import { FormattedMessage, useIntl } from '~/techstyle-shared/react-intl';
import { useSession } from '~/techstyle-shared/redux-core';

import { PromoPricingMessage } from '../PromoPricingMessage';

import errorMessages from './errorMessages';
import {
  useProductDetailCTAContext,
  ProductDetailCTAErrorTypes,
} from './ProductDetailCTAContext';
import VIPOptInMessage from './VIPOptInMessage';

const Wrapper = styled.div`
  ${({ theme }) => theme.pdpStyles.vipCtaWrapper}
  ${({ $isSingleCtaVipOptInEnabled }) =>
    $isSingleCtaVipOptInEnabled &&
    css`
      background: none;
      border: none;
      padding: 0;
    `}
`;

const PromoMessageWrapper = styled.div``;

const VIPAddToBagButton = styled(Button).attrs({
  variant: 'primaryAddToBag',
  size: 'lg',
})`
  margin: 12px 0;
  color: ${({ theme }) => theme.colors.textLight};
`;

const promoPriceStyle = css`
  ${({ theme }) => theme.pdpStyles.promoMessage.copy};
`;

const promoMessageStyle = css`
  ${({ theme }) => theme.pdpStyles.promoMessage.copy};
`;

const priceStyle = css`
  font-weight: 700;
`;

const labelStyle = css`
  font-size: 18px;
  font-weight: bold;
  letter-spacing: ${-0.4 / 18}em;
`;

const InStockAtbButtonCopy = () => {
  const { enabled: isSingleCtaVipOptInEnabled } = useHasFeature(
    featureNames.SINGLE_CTA_VIP_OPT_IN
  );
  return (
    <>
      {isSingleCtaVipOptInEnabled ? (
        <FormattedMessage
          id="vip_opt_in.add_to_bag"
          defaultMessage="Add to Bag"
        />
      ) : (
        <FormattedMessage
          id="global_cta.add_to_bag_vip"
          defaultMessage="VIP Add to Bag"
        />
      )}
    </>
  );
};

function VIPOptIn({ ...rest }) {
  const { isLoggedIn } = useSession();
  const { isVip } = useMembership();
  const intl = useIntl();

  const {
    addToCart,
    handleSharedValidation,
    isForceDisableCtaEnabled,
    isLoading,
    isOutOfStock,
    signupAsVIPCart,
    setErrorMessage,
    hasError,
  } = useProductDetailCTAContext();

  const disabled = isLoading || isOutOfStock || isForceDisableCtaEnabled;

  useEffect(() => {
    if (!hasError) {
      setErrorMessage(null);
    }
  }, [hasError, setErrorMessage]);

  const handleCTAClick = useCallback(async () => {
    try {
      if (!isLoggedIn) {
        // storing in the context to remember to add to VIP cart after signup
        await signupAsVIPCart();
        await handleSharedValidation();
      } else {
        await handleSharedValidation();
        await addToCart({ enableVIPCart: true });
      }
    } catch (error) {
      if (error.message === ProductDetailCTAErrorTypes.SelectorError) {
        setErrorMessage(intl.formatMessage(errorMessages.sizeSelectorError));
      }
    }
  }, [
    addToCart,
    handleSharedValidation,
    intl,
    isLoggedIn,
    setErrorMessage,
    signupAsVIPCart,
  ]);

  const product = useProductContext();
  const promoPricingConfig = config.get('public.brand.promoPricingUI');

  const { enabled: isPrioritizedFeatureEnabled } = useHasFeature(
    featureNames.PRIORITIZED_PROMOS
  );
  const isPrioritizedEnabled =
    isPrioritizedFeatureEnabled && promoPricingConfig?.showPromoPricingOffer;

  const { enabled: isSingleCtaVipOptInEnabled } = useHasFeature(
    featureNames.SINGLE_CTA_VIP_OPT_IN
  );

  const offerLabelContainerName = getPromoPricingUIAssetContainerName(
    promoPricingConfig,
    isVip
  );
  const assetContainerOfferLabel = useAsset(offerLabelContainerName);

  const promoPricingActiveAsset = getPromoPricingActiveAsset({
    isPrioritizedEnabled,
    assetContainerOfferLabel,
    product,
  });

  const isPromoPricingActive = Boolean(promoPricingActiveAsset);
  const pdpLabel = promoPricingActiveAsset?.options?.customVars?.pdpLabel;
  const htmlText = promoPricingActiveAsset?.options?.htmlText;

  return (
    <Wrapper $isSingleCtaVipOptInEnabled={isSingleCtaVipOptInEnabled} {...rest}>
      {!isSingleCtaVipOptInEnabled && (
        <PromoMessageWrapper>
          {isPromoPricingActive ? (
            <PromoPricingMessage
              promoPriceStyle={promoPriceStyle}
              pdpLabel={pdpLabel}
              isPromoPricingActive={isPromoPricingActive}
              priceStyle={priceStyle}
              htmlText={htmlText}
              product={product}
            />
          ) : (
            <PromoMessage
              showPromoPrice
              promoPriceStyle={promoPriceStyle}
              promoMessageStyle={promoMessageStyle}
            />
          )}
        </PromoMessageWrapper>
      )}
      <VIPAddToBagButton
        variant="primaryAddToBag"
        labelStyle={labelStyle}
        onClick={handleCTAClick}
        data-autotag="pdp-vip-optin-add-to-bag"
        disabled={disabled}
        data-cnstrc-btn="add-to-cart"
      >
        {isOutOfStock ? (
          <FormattedMessage
            id="global_cta.out_of_stock"
            defaultMessage="Out of Stock"
          />
        ) : (
          <InStockAtbButtonCopy />
        )}
      </VIPAddToBagButton>{' '}
      {!isSingleCtaVipOptInEnabled && <VIPOptInMessage />}
    </Wrapper>
  );
}

export default VIPOptIn;
